import { fetchAuthSession, signIn } from "aws-amplify/auth";
import { computed } from "vue";
import {
  type LocationQueryValue,
  type RouteLocationNormalized,
  useRoute,
} from "vue-router";

export const normalizeQueryParam = (
  value: LocationQueryValue | LocationQueryValue[] | undefined,
): string | undefined => {
  if (Array.isArray(value)) {
    return value[0] ?? undefined;
  }

  return value ?? undefined;
};

export const buildSwaggerRedirectUrl = (
  redirectUri: string,
  accessToken: string,
  state?: string,
) => {
  const url = new URL(redirectUri);
  const params = new URLSearchParams();
  params.set("access_token", accessToken);
  params.set("token_type", "bearer");
  if (state) {
    params.set("state", state);
  }
  url.hash = params.toString();

  return url.toString();
};

/**
 * Route guard helper for the `/login` page.
 *
 * When running in local-backend mode and the route contains a `redirect_uri`
 * query param (i.e. Swagger is initiating an OAuth implicit flow), this
 * function checks for an existing Amplify session. If the user is already
 * authenticated it redirects straight back to Swagger with the token —
 * bypassing the login form entirely. Otherwise it returns `undefined` so the
 * login page renders normally.
 *
 * Returns `false` (abort navigation) after performing the `window.location`
 * redirect, or `undefined` to let the navigation continue.
 */
export const handleSwaggerOAuthBeforeEnter = async (
  to: RouteLocationNormalized,
): Promise<false | undefined> => {
  const isLocal = import.meta.env.VITE_MODE === "local-backend";

  if (!isLocal || !to.query.redirect_uri) {
    return undefined;
  }

  try {
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();

    if (accessToken) {
      window.location.href = buildSwaggerRedirectUrl(
        normalizeQueryParam(to.query.redirect_uri)!,
        accessToken,
        normalizeQueryParam(to.query.state),
      );

      return false;
    }
  } catch {}

  return undefined;
};

export const useSwaggerOAuth = () => {
  const route = useRoute();

  const redirectUri = computed(() =>
    normalizeQueryParam(route.query.redirect_uri),
  );
  const oauthState = computed(() => normalizeQueryParam(route.query.state));
  const isSwaggerOAuthRequest = computed(() => !!redirectUri.value);

  const completeSwaggerOAuth = async (
    email: string,
    password: string,
    onError: (e: unknown) => void,
  ) => {
    try {
      await signIn({
        username: email,
        password,
        options: {
          authFlowType: "USER_PASSWORD_AUTH",
        },
      });

      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (!accessToken) {
        onError(
          new Error(
            "Authentication succeeded but no access token was returned.",
          ),
        );

        return;
      }

      if (!redirectUri.value) {
        onError(
          new Error(
            "Missing redirect_uri parameter. Please open this page from Swagger UI.",
          ),
        );

        return;
      }

      window.location.href = buildSwaggerRedirectUrl(
        redirectUri.value,
        accessToken,
        oauthState.value,
      );
    } catch (e) {
      onError(e);
    }
  };

  return {
    isSwaggerOAuthRequest,
    completeSwaggerOAuth,
  };
};
